import React, { useState, useMemo } from 'react';
import { FORMS } from '../../schemas/stepMap';
import FormBuilder from '../../components/formBuilder';
import { useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import BackForwardButtons from '../../components/buttons/BackForwardButtons';
import { useSteps } from '../../hooks/useSteps';
import SummaryProjection from './SummaryProjection';
import graphDataFormatter from './Comparison/graphDataFormatter';
import { projectionCalculator } from './Comparison/projectionCalculator';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { isValue } from '../../utility/isValue';
import { useUser } from '../../hooks/useUser';
import InvestmentMetrics from '../../components/InvestmentMetrics';

const graphSelectOptions = [
  {
    name: 'netMonthlyIncome',
    altName: 'monthlyNOI',
    description: 'New Monthly Income After Operating Expenses',
    label: 'Net Income',
  },
  {
    name: 'equityInProperty',
    altName: 'totalEquity',
    description:
      'Total Equity Based on the Estimated Market Value of the Property',
    label: 'Total Equity',
  },
  {
    name: 'newMonthlyPreTaxCashFlow',
    altName: 'monthlyPreTaxCashFlowBuyAndHold',
    description: 'Annual Pre-Tax Cash Flow',
    label: 'Cash Flow',
  },
  {
    name: 'newTotalRoiYear1',
    altName: 'totalROIYear1BuyAndHoldAmount',
    description: 'Return On Investment',
    label: 'ROI',
  },
  {
    name: 'newMarketValue',
    altName: 'propertyValueProjection',
    description: '',
    label: 'Property Value',
  },
  {
    type: 'stack',
    stackIncludes: [
      {
        name: 'yearOnePrincipalReduction',
        altName: 'newYearOnePrincipalReduction',
        label: 'Principal',
      },
      {
        name: 'interest',
        label: 'Interest',
        derivedFrom: [
          'monthlyDebtService',
          'balloonPayment',
          'yearOnePrincipalReduction',
          'newYearOnePrincipalReduction',
          'newMonthlyPayment',
        ],
        calcFunction: (
          monthlyDebtService,
          balloonPayment,
          yearOnePrincipalReduction,
          newYearOnePrincipalReduction,
          newMonthlyPayment
        ) => {
          if (isValue(monthlyDebtService, yearOnePrincipalReduction)) {
            return (
              monthlyDebtService +
              (balloonPayment || 0) -
              yearOnePrincipalReduction
            );
          }

          if (isValue(newMonthlyPayment, newYearOnePrincipalReduction)) {
            return (
              newMonthlyPayment +
              (balloonPayment || 0) -
              newYearOnePrincipalReduction
            );
          }

          return null;
        },
      },
    ],
    description: 'Mortgage Payments over the years',
    label: 'Amortization',
  },
];

export default function BrrrrSummary() {
  const { propertyData, scenarios } = useOutletContext();
  const scenarioData = scenarios.find((scenario) => !scenario.isDefault);
  const { pathname } = useLocation();
  const { user, userDocData } = useUser();

  if (!scenarioData?.refinancingWorksheetSubmitted) {
    return (
      <div className="w-full flex flex-col items-center justify-center mt-36">
        <h2 className="mb-6 text-xl font-medium text-primary">
          No BRRRR Analysis Created
        </h2>
        {!pathname.includes('sharing') && (
          <p className="mb-72 w-2/3 text-center">
            Once you have filled out the{' '}
            <Link
              to="../../purchase-worksheet"
              className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
            >
              purchase worksheet
            </Link>
            , the{' '}
            <Link
              to="../../rehab-worksheet"
              className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
            >
              rehab worksheet
            </Link>
            , and the{' '}
            <Link
              to="../../refinancing"
              className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
            >
              refinancing worksheet
            </Link>
            , you will be able to see your BRRRR analysis here.
          </p>
        )}
      </div>
    );
  }

  const { prev, curr, next } = useSteps();

  const rehabYears = scenarioData?.rehabLength
    ? Math.floor(scenarioData?.rehabLength / 12)
    : 0;

  const BRRRRAnalysisForm = curr || FORMS.BRRRRAnalysis;
  const BRRRRAnalysisSchema = BRRRRAnalysisForm.schema;

  const [graph, setGraph] = useState(0);
  const [years, setYears] = useState(30);

  const projectionForm = FORMS.projection;

  const brrrrSchema = projectionForm.schema.calculations.schema[1];
  const brrrrFields = brrrrSchema.schema;

  const buyAndHoldSchema = projectionForm.schema.calculations.schema[2];
  const buyAndHoldFields = buyAndHoldSchema.schema;

  const BRRRRData = useMemo(
    () =>
      projectionCalculator(
        years,
        brrrrFields,
        {
          ...propertyData,
          ...scenarioData,
        },
        rehabYears
      ),
    [years, propertyData, scenarioData]
  );

  const buyAndHoldData = rehabYears
    ? projectionCalculator(rehabYears, buyAndHoldFields, {
        ...propertyData,
        ...scenarioData,
      })
    : null;

  const projectionData = buyAndHoldData
    ? buyAndHoldData.concat(BRRRRData)
    : BRRRRData;

  const graphData = graphDataFormatter(
    years,
    projectionData,
    {},
    {},
    graphSelectOptions[graph]?.name,
    graphSelectOptions[graph]?.altName || null,
    null,
    'single',
    graphSelectOptions[graph]
  );

  return (
    <>
      <Helmet>
        <title>PropertyCast.io - BRRRR Analysis</title>
      </Helmet>
      <div>
        {user && !userDocData.loading && (
          <InvestmentMetrics
            schema={BRRRRAnalysisForm.investmentMetrics}
            data={{ ...userDocData, ...propertyData, ...scenarioData }}
          />
        )}
      </div>
      <div className="pt-4 pb-2">
        <FormBuilder
          formType={BRRRRAnalysisForm.formType}
          calculationsSchema={BRRRRAnalysisSchema}
          beforeData={propertyData}
          afterData={scenarioData}
          keyMetricsSchema={BRRRRAnalysisForm.keyMetrics}
          keyMetricsData={{ ...propertyData, ...scenarioData }}
        />
        <div>
          <SummaryProjection
            options={graphSelectOptions}
            currOption={graph}
            setOption={setGraph}
            years={years}
            setYears={setYears}
            graphData={graphData}
            tableData={projectionData}
            schema={brrrrSchema}
            rehabYears={rehabYears}
            tableLabel="Detailed Projections"
          />
        </div>
      </div>
      <BackForwardButtons prev={prev} next={next} />
    </>
  );
}
